import { useState, useEffect } from "react";
import { Home, User, DollarSign, GraduationCap, MessagesSquare } from "lucide-react";

const NAV_LINKS = [
  { label: "Asosiy", id: "home", icon: Home },
  { label: "Kurs haqida", id: "course", icon: GraduationCap }, // Avval nima o'rganishini ko'rsin
  { label: "Mentor", id: "mentor", icon: User },          // Keyin kim o'rgatishini
  { label: "Narxlar", id: "pricing", icon: DollarSign },    // Oxirida narxi
  { label: "Savollar", id: "faq", icon: MessagesSquare },     // FAQ bo'limi

];

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

// Hero'dagi video faqat 768px+ ekranda ko'rinadi — nav shu holatga mos ravishda "shisha" rejimga o'tadi
function useIsDesktop(breakpoint = 768) {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    setIsDesktop(mq.matches);
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isDesktop;
}

export default function Nav({ nav, scrollToForm }) {
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false); // Hero bo'limi (id="home") ekrandan chiqib ketdimi
  const [active, setActive] = useState("home");
  const isDesktop = useIsDesktop();

  // Desktop: Hero video ko'rinib turgan ekanda (hali undan chiqmagan) — shisha holat
  // Mobil: Hero'da video yo'q, shuning uchun eski 20px chegarasi bilan ishlaydi
  const glass = isDesktop && !pastHero;
  const showWhiteNav = pastHero || (!isDesktop && scrolled);

  // Nav.jsx da useEffect ichini shu bilan almashtiring:
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);

      const heroEl = document.getElementById("home");
      if (heroEl) {
        const rect = heroEl.getBoundingClientRect();
        setPastHero(rect.bottom <= 64); // 64px = nav balandligi (h-16)
      }

      const scrollY = window.scrollY + window.innerHeight / 2; // ← o'rtadan hisoblash

      for (const link of [...NAV_LINKS].reverse()) {
        const el = document.getElementById(link.id);
        if (el && scrollY >= el.getBoundingClientRect().top + window.scrollY) {
          setActive(link.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // boshlang'ich holatni ham hisoblab qo'yamiz
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* TOP NAV */}
      <nav className={[
        "fixed top-0 left-0 right-0 z-50 rounded-b-3xl h-16 transition-all duration-300",
        showWhiteNav
          ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)]"
          : glass
            ? "bg-white/10 backdrop-blur-xl border-b border-white/15"
            : "bg-transparent",
      ].join(" ")}>
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-5">
          <span className={"text-lg font-extrabold tracking-tight transition-colors " + (glass ? "text-white" : "text-violet-600")}>
            {nav.logo}
          </span>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className={[
                  "rounded-xl px-4 flex gap-1 items-center py-2 text-sm font-semibold transition-all",
                  glass
                    ? (active === link.id
                        ? "bg-white/20 text-white"
                        : "text-white/80 hover:bg-white/10 hover:text-white")
                    : (active === link.id
                        ? "bg-violet-50 text-violet-600"
                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"),
                ].join(" ")}>
                {link.icon && (
                  <link.icon
                    size={16}
                    className={glass ? "text-white/80" : (active === link.id ? "text-violet-600" : "text-gray-400")}
                  />
                )}
                {link.label}
              </button>
            ))}
          </div>

          <span className={"hidden sm:inline md:hidden text-xs font-bold px-3 py-1.5 rounded-full transition-colors " +
            (glass ? "text-white border-[1.5px] border-white/25 bg-white/10" : "text-violet-600")}
            style={glass ? {} : { background: "#f5f3ff", border: "1.5px solid #ede9fe" }}>
            {nav.badge}
          </span>

          <button
            onClick={scrollToForm}
            className="hidden md:inline-flex rounded-xl bg-violet-600 px-5 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-lg hover:shadow-violet-200">
            {nav.cta} →
          </button>

          <button
            onClick={scrollToForm}
            className="md:hidden rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white transition-all active:scale-95">
            {nav.cta}
          </button>
        </div>
      </nav>

      {/* MOBILE BOTTOM NAV */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <div className="absolute rounded-t-3xl inset-0 bg-white/95 backdrop-blur-xl border-t border-gray-100" />
        <div className="relative flex items-center justify-around px-2 py-2 pb-[env(safe-area-inset-bottom)]">
          {NAV_LINKS.map((link) => {
            const LinkIcon = link.icon;
            const isActive = active === link.id;
            //mobile da faq ni ignor qilishi kerak
            if (link.id === "faq") return null;
            return (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="flex flex-1 flex-col items-center gap-0.5 rounded-2xl py-2 transition-all active:scale-95">
                <div className={[
                  "flex h-8 w-8 items-center justify-center rounded-xl transition-all",
                  isActive ? "bg-violet-100" : "",
                ].join(" ")}>
                  <LinkIcon
                    size={20}
                    className={isActive ? "text-violet-600" : "text-gray-400"}
                    strokeWidth={isActive ? 2.5 : 1.8}
                  />
                </div>
                <span className={[
                  "text-[10.5px] font-semibold leading-none transition-all",
                  isActive ? "text-violet-600" : "text-gray-400",
                ].join(" ")}>
                  {link.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}